import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import './Login.css';
import myshieldLogo from '../assets/myshield-logo.png';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'This link is invalid or has expired. Please request a new one.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="login-container">
        <video className="video-background" autoPlay loop muted playsInline disablePictureInPicture>
          <source src="/background-video.mp4" type="video/mp4" />
        </video>
        <div className="video-overlay" />
        <div className="login-box visible">
          <div className="login-logo"><img src={myshieldLogo} alt="Myshield Logo" /></div>
          <div className="error-message">Invalid reset link. Please request a new password reset.</div>
          <button className="login-button" style={{ marginTop: 16 }} onClick={() => navigate('/forgot-password')}>
            Request New Link
          </button>
          <div className="login-footer"><p>© 2026 Yourshield Ltd. All rights reserved.</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <video
        className="video-background"
        autoPlay
        loop
        muted
        playsInline
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback"
        onContextMenu={(e) => e.preventDefault()}
      >
        <source src="/background-video.mp4" type="video/mp4" />
      </video>
      <div className="video-overlay" />

      <div className="login-box visible">
        <div className="login-logo">
          <img src={myshieldLogo} alt="Myshield Logo" />
        </div>

        {done ? (
          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
            <h3 style={{ margin: '0 0 10px', color: '#002060', fontSize: '17px' }}>Password reset!</h3>
            <p style={{ color: '#4a5568', fontSize: '14px', lineHeight: 1.6, margin: '0 0 20px' }}>
              Your password has been updated. You can now sign in with your new password.
            </p>
            <button className="login-button" onClick={() => navigate('/login')}>
              Sign In
            </button>
          </div>
        ) : (
          <>
            <h3 style={{ margin: '0 0 6px', color: '#002060', fontSize: '17px' }}>Set a new password</h3>
            <p style={{ color: '#718096', fontSize: '13px', margin: '0 0 20px', lineHeight: 1.5 }}>
              Choose a strong password of at least 6 characters.
            </p>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <input
                  type="password"
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">Confirm Password</label>
                <input
                  type="password"
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" className="login-button" disabled={loading || !newPassword || !confirmPassword}>
                {loading ? 'Saving…' : 'Reset Password'}
              </button>
            </form>
          </>
        )}

        <div className="login-footer">
          <p>© 2026 Yourshield Ltd. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
